import React, { Fragment, memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { DataRow } from 'src/components/shared/DataRow'
import { WebhookDeliveryResultCell } from 'src/components/webhook/grid/WebhookDeliveryResultCell'
import { webhookEventLabels, webhookResourceTypeLabels } from 'src/constants/webhook/constants'
import type { WebhookDelivery } from 'src/interfaces/webhook'
import { formatDateTime, parseUnixTimestamp } from 'src/utils/grid'

interface Props {
  delivery: WebhookDelivery | null
  onClose(): void
}

interface DetailRow {
  label: string
  value: string
}

/**
 * One delivery's facts in a `CommonModal`: flat label/value rows (`DataRow`), the two JSON blobs
 * under them when present, a single Close in the footer. Opened by the eye button in the
 * Deliveries grid's actions column. A modal rather than a page because there is nothing nested
 * to drill into — see admin-patterns.md § Record page for when a row gets a page instead.
 */
const WebhookDeliveryDetailsModalComponent = ({ delivery, onClose }: Props) => {
  // `event` and `subscription` are optional left-joined relations, so every read is guarded and
  // renders an em-dash when absent — a cosmetic default, not substituted business content.
  const rows = useMemo<DetailRow[]>(() => {
    if (!delivery) {
      return []
    }

    const eventType = delivery.event?.eventType

    return [
      { label: 'Attempt', value: String(delivery.attempt) },
      {
        label: 'Response status',
        value: delivery.responseStatus === undefined ? '—' : String(delivery.responseStatus),
      },
      {
        label: 'Latency',
        value: delivery.latency === undefined ? '—' : `${delivery.latency} ms`,
      },
      {
        label: 'Sent at',
        value: formatDateTime(parseUnixTimestamp(delivery.createdAt)),
      },
      {
        label: 'Event',
        value: eventType ? webhookEventLabels[eventType] ?? eventType : '—',
      },
      {
        label: 'Resource type',
        value: delivery.event?.resourceType
          ? webhookResourceTypeLabels[delivery.event.resourceType] ?? delivery.event.resourceType
          : '—',
      },
      { label: 'Resource ID', value: delivery.event?.resourceId ?? '—' },
      { label: 'Callback URL', value: delivery.subscription?.callbackUrl ?? '—' },
      { label: 'Subscription ID', value: delivery.subscriptionId },
      { label: 'Event ID', value: delivery.eventId },
      { label: 'Delivery ID', value: delivery.deliveryId },
    ]
  }, [delivery])

  const responsePayload = useMemo(() => {
    if (!delivery?.responsePayload) {
      return null
    }

    return JSON.stringify(delivery.responsePayload, null, 2)
  }, [delivery])

  const eventDetails = useMemo(() => {
    const details = delivery?.event?.eventDetails

    if (!details || Object.keys(details).length === 0) {
      return null
    }

    return JSON.stringify(details, null, 2)
  }, [delivery])

  return (
    <CommonModal
      isOpen={Boolean(delivery)}
      title="Delivery details"
      className={tw`max-w-[560px]`}
      onClose={onClose}
      buttons={
        <Button
          size="small"
          color="grey"
          onClick={onClose}
          data-testid="webhook-delivery-details-close-button"
        >
          Close
        </Button>
      }
    >
      <div data-testid="webhook-delivery-details-modal">
        {delivery ? (
          <div css={tw`mb-2`}>
            <WebhookDeliveryResultCell deliveryStatus={delivery.deliveryStatus} />
          </div>
        ) : null}
        {rows.map((row) => {
          return (
            <DataRow
              key={row.label}
              title={row.label}
              text={row.value}
              textClassName={tw`break-all text-right`}
            />
          )
        })}
        {eventDetails ? (
          <Fragment>
            <Typography variant="h6" css={tw`mt-4 text-shades-400`}>
              Event details
            </Typography>
            <pre
              css={tw`mt-1 overflow-x-auto rounded-lg bg-shades-50 p-3 text-xs`}
              data-testid="webhook-delivery-event-details"
            >
              {eventDetails}
            </pre>
          </Fragment>
        ) : null}
        {responsePayload ? (
          <Fragment>
            <Typography variant="h6" css={tw`mt-4 text-shades-400`}>
              Response from the customer
            </Typography>
            <pre
              css={tw`mt-1 overflow-x-auto rounded-lg bg-shades-50 p-3 text-xs`}
              data-testid="webhook-delivery-response-payload"
            >
              {responsePayload}
            </pre>
          </Fragment>
        ) : null}
      </div>
    </CommonModal>
  )
}

export const WebhookDeliveryDetailsModal = memo(WebhookDeliveryDetailsModalComponent)
